import { trackUserEvent } from '@vend/utilities'
import { useCallback, useEffect, useState } from 'react'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { IPromise } from 'angular'
import { hasContactInfo } from '../react/modals/common/customer-contact-utils'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import {
  selectCurrentSale,
  selectCustomer,
  selectLineItems,
} from '../../state/slices/currentSale/currentSaleSlice'
import { containsGiftCard } from '../../functions/lineItem'
import {
  focusGlobalSearch,
  useIsCalculatingSale,
  useRetailerSettingsManager,
  useSaleManager,
  useTrainingModeManager,
  useUserSwitchingService,
} from '../../ng-compat/hooks'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import {
  finalisingSale,
  finalisingSaleComplete,
} from '../../state/slices/uiStateSlice'
import { clientApiEventBus } from '../../managers/client-api-event-bus'
import { WEB_REGISTER } from '../web-register.constant'
import { formatToClientApi } from '../../functions/sale-transformer'
import {
  hasLineItems,
  hasPayments,
  isValidCustomer,
} from '../../functions/sale'
import { print } from '../print/print'
import { useSaleModals } from './useSaleModals'

export type MoreActionsItemName =
  | 'QUOTE'
  | 'SERVICE'
  | 'DISCARD'
  | 'UNFULFILLED'

export const useMoreSaleActions = () => {
  const t = useT()
  const dispatch = useAppDispatch()
  const retailerSettingsManager = useRetailerSettingsManager()
  const calculating = useIsCalculatingSale()
  const saleManager = useSaleManager()
  const trainingModeManager = useTrainingModeManager()
  const userSwitchingService = useUserSwitchingService()
  const saleCustomer = useAppSelector(selectCustomer)
  const sale = useAppSelector(selectCurrentSale)
  const lineItems = useAppSelector(selectLineItems)
  const {
    openQuoteCustomerModal,
    openServiceModal,
    openMarkUnfulfilledModal,
  } = useSaleModals()
  const [checkQuoteCanComplete, setCheckQuoteCanComplete] = useState(false)

  useEffect(() => {
    if (checkQuoteCanComplete) {
      setCheckQuoteCanComplete(false)
      if (hasContactInfo(saleCustomer)) {
        saleManager.quoteSale().then(() => {
          $state.go(STATES.SALE_CONFIRMATION)
        })
      }
    }
  }, [checkQuoteCanComplete, saleCustomer, saleManager])

  const quoteSale = useCallback(async () => {
    trackUserEvent('Quotes', 'Clicked: Create Quote')
    if (containsGiftCard(lineItems)) {
      ToastNotifications.negative(
        t(
          `Quotes can’t include gift cards. Remove the gift card and try again.`
        )
      )
      return
    }
    if (hasContactInfo(saleCustomer)) {
      await saleManager.quoteSale()
      $state.go(STATES.SALE_CONFIRMATION)
    } else {
      const onClose = async (complete: IPromise<any> | undefined) => {
        if (!complete) {
          return
        }

        // This is an angularjs $q promise so we can't await it.
        // TODO: Once Sale component is rolled out, QuoteCustomerModal can
        // be converted to use a native Promise.
        complete
          .then(async () => {
            // Have to check if we have a user attached on the next render
            setCheckQuoteCanComplete(true)
          })
          .catch(() => {
            return
          })
      }
      openQuoteCustomerModal(onClose)
    }
  }, [lineItems, openQuoteCustomerModal, saleCustomer, saleManager, t])

  const createService = useCallback(async () => {
    trackUserEvent(
      'Services',
      'sale.service_sale.new_sale.create_service.click'
    )

    openServiceModal()
  }, [openServiceModal])

  const discardSale = useCallback(async () => {
    if (calculating) {
      return
    }

    dispatch(finalisingSale())

    trackUserEvent(WEB_REGISTER.APPLICATION_NAME, 'Sale Action Buttons', {
      saleState: sale.displayStatus,
      button: 'Discard Sale',
    })

    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_DISCARD,
      data: {
        sale: formatToClientApi(
          sale,
          lineItems,
          retailerSettingsManager.get().discount_product_id
        ),
      },
    })

    const isNewSale = !sale.id

    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_DISCARD,
      data: formatToClientApi(
        sale,
        lineItems,
        retailerSettingsManager.get().discount_product_id
      ),
    })
    if (isNewSale && !hasLineItems(sale)) {
      await saleManager.clearSale()
    } else if (isNewSale && !hasPayments(sale)) {
      await saleManager.clearSale()
    } else {
      if (hasPayments(sale)) {
        const trackingEvent = 'Discard Sale and Print Receipt'
        if (trainingModeManager.trainingModeOnboardingState) {
          trackUserEvent(
            WEB_REGISTER.APPLICATION_NAME,
            trainingModeManager.currentTourType || '',
            { step: trackingEvent }
          )
        } else {
          trackUserEvent(WEB_REGISTER.APPLICATION_NAME, trackingEvent)
        }

        const data = await saleManager.getReceiptData()
        await print({ type: 'receipt-settings-and-template', data })
      }
      await saleManager.voidSale()
    }
    await userSwitchingService.switchUserIfNeeded()
    focusGlobalSearch()
    dispatch(finalisingSaleComplete())
  }, [
    calculating,
    dispatch,
    lineItems,
    retailerSettingsManager,
    sale,
    saleManager,
    trainingModeManager.currentTourType,
    trainingModeManager.trainingModeOnboardingState,
    userSwitchingService,
  ])

  const markUnfulfilled = useCallback(() => {
    if (containsGiftCard(lineItems)) {
      ToastNotifications.negative(
        t(
          `Gift cards can't be marked for fulfillment. Remove the gift card, then try again.`
        )
      )
      return
    }
    trackUserEvent(
      WEB_REGISTER.APPLICATION_NAME,
      'Opened Mark Sale as Unfulfilled Modal',
      {
        saleId: sale.id,
        customerAttached: isValidCustomer(saleCustomer),
        openedFrom: 'moreActionsDropdown',
      }
    )

    openMarkUnfulfilledModal()
  }, [lineItems, openMarkUnfulfilledModal, sale.id, saleCustomer, t])

  const handleActions = useCallback(
    (item: MoreActionsItemName) => {
      switch (item) {
        case 'QUOTE':
          return quoteSale()
        case 'SERVICE':
          return createService()
        case 'DISCARD':
          discardSale()
          break
        case 'UNFULFILLED':
          markUnfulfilled()
          break
      }
    },
    [createService, discardSale, markUnfulfilled, quoteSale]
  )

  return handleActions
}
